import CancelRoundedIcon from "@mui/icons-material/CancelRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded"
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded"
import WatchLaterRoundedIcon from "@mui/icons-material/WatchLaterRounded"
import { Box, Button, Stack, Typography } from "@mui/material"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { INSTALMENT_VIEW, INSTALMENT_VIEW_CHIP } from "./constants"
import { formatAmount, formatMonth, formatShortDate } from "./format"
import { canPayOnline } from "./payment"
import { getInstalmentView } from "./selectors"
import schemeTokens from "./tokens"

const VIEW_ICON = {
  [INSTALMENT_VIEW.PAID]: CheckCircleRoundedIcon,
  [INSTALMENT_VIEW.DUE_NOW]: WatchLaterRoundedIcon,
  [INSTALMENT_VIEW.IN_GRACE]: WatchLaterRoundedIcon,
  [INSTALMENT_VIEW.OVERDUE]: WatchLaterRoundedIcon,
  [INSTALMENT_VIEW.SCHEDULED]: RadioButtonUncheckedRoundedIcon,
  [INSTALMENT_VIEW.UPCOMING]: RadioButtonUncheckedRoundedIcon,
  [INSTALMENT_VIEW.MISSED]: CancelRoundedIcon,
  [INSTALMENT_VIEW.CANCELLED]: CancelRoundedIcon,
  [INSTALMENT_VIEW.BONUS_PENDING]: RedeemRoundedIcon,
  [INSTALMENT_VIEW.BONUS_CREDITED]: RedeemRoundedIcon,
}

const getSubLine = (instalment, view) => {
  switch (view) {
    case INSTALMENT_VIEW.PAID:
      return instalment.markedPaidAt
        ? `${STRING_CONSTANTS.PAID_ON} ${formatShortDate(instalment.markedPaidAt)}`
        : STRING_CONSTANTS.PAID
    case INSTALMENT_VIEW.DUE_NOW:
      return STRING_CONSTANTS.DUE_TODAY
    case INSTALMENT_VIEW.IN_GRACE:
      return instalment.graceEndDate
        ? `${STRING_CONSTANTS.PAY_BY} ${formatShortDate(instalment.graceEndDate)}`
        : STRING_CONSTANTS.IN_GRACE_PERIOD
    case INSTALMENT_VIEW.OVERDUE:
      return `${STRING_CONSTANTS.DUE_ON} ${formatShortDate(instalment.dueDate)}`
    case INSTALMENT_VIEW.MISSED:
      return STRING_CONSTANTS.GRACE_PERIOD_ELAPSED
    case INSTALMENT_VIEW.CANCELLED:
      return STRING_CONSTANTS.SCHEME_CANCELLED_NOTE
    case INSTALMENT_VIEW.BONUS_CREDITED:
      return `${STRING_CONSTANTS.RETAILER_CONTRIBUTION} · ${
        STRING_CONSTANTS.PAID_ON
      } ${formatShortDate(instalment.markedPaidAt)}`
    case INSTALMENT_VIEW.BONUS_PENDING:
      return STRING_CONSTANTS.RETAILER_CONTRIBUTION
    default:
      return `${STRING_CONSTANTS.DUE_ON} ${formatShortDate(instalment.dueDate)}`
  }
}

const InstalmentRow = ({ instalment, isLast, payLabel, paying, onPay }) => {
  const view = getInstalmentView(instalment)
  const chip = INSTALMENT_VIEW_CHIP[view] || {}
  const Icon = VIEW_ICON[view] || RadioButtonUncheckedRoundedIcon
  const isBonus =
    view === INSTALMENT_VIEW.BONUS_PENDING ||
    view === INSTALMENT_VIEW.BONUS_CREDITED

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.5,
        borderBottom: isLast ? "none" : "1px solid",
        borderColor: schemeTokens.color.border,
      }}
    >
      <Icon sx={{ fontSize: 20, color: chip.color, flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
          {formatMonth(instalment.dueDate)}
        </Typography>
        <Typography sx={schemeTokens.text.hint}>
          {getSubLine(instalment, view)}
        </Typography>
        {payLabel && (
          <Button
            variant="contained"
            size="small"
            disabled={paying}
            onClick={onPay}
            sx={{
              mt: 1,
              fontWeight: 700,
              borderRadius: schemeTokens.radius.control,
            }}
          >
            {payLabel}
          </Button>
        )}
      </Box>

      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: isBonus ? "success.main" : "text.primary",
          }}
        >
          {formatAmount(instalment.amount)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: chip.color }}>
          {chip.label}
        </Typography>
      </Box>
    </Stack>
  )
}

const getPayLabel = (scheme, metrics, onPay) =>
  onPay && canPayOnline(scheme, metrics)
    ? `${STRING_CONSTANTS.PAY} ${formatAmount(metrics.monthlyAmount)}`
    : null

const SchemeInstalments = ({ scheme, metrics, paying, onPay }) => (
  <Box
    sx={{
      borderRadius: schemeTokens.radius.card,
      border: "1px solid",
      borderColor: schemeTokens.color.border,
      bgcolor: schemeTokens.color.surface,
      px: 2,
      py: 1.5,
    }}
  >
    <Typography sx={{ ...schemeTokens.text.sectionTitle, mb: 0.5 }}>
      {STRING_CONSTANTS.INSTALMENTS}
    </Typography>

    {metrics.allInstalments.map((instalment, index) => (
      <InstalmentRow
        key={instalment.id || instalment.installmentNo}
        instalment={instalment}
        isLast={index === metrics.allInstalments.length - 1}
        payLabel={
          instalment.installmentNo === metrics.currentDue?.installmentNo
            ? getPayLabel(scheme, metrics, onPay)
            : null
        }
        paying={paying}
        onPay={onPay}
      />
    ))}
  </Box>
)

export default SchemeInstalments
